import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { ArrowDownCircle, ArrowUpCircle, Database, Zap } from 'lucide-react';

/**
 * DataFlowVisualization Component
 * Horizontal flow diagram with curved arrows for non-linear data connections
 * Style inspired by FlowVisualization with smooth curves from reference design
 */

const DataFlowVisualization = ({
  nodes = [],
  workflowSteps = [],
  isDarkMode = false,
  agentDefinition = null,
  currentNodeIndex = 0,
  onNodeClick = null
}) => {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [hoveredConnection, setHoveredConnection] = useState(null);
  const [hoveredLabel, setHoveredLabel] = useState(null);
  const [nodePositions, setNodePositions] = useState({});
  const nodesContainerRef = useRef(null);

  // Get node details from workflowSteps
  const getNodeDetails = (index) => {
    const step = workflowSteps[index];
    if (!step) return { hasIntegration: false, hasAgentOutput: false, nodeType: null };

    const nodeDetails = step.node?.node_details || {};
    return {
      hasIntegration: nodeDetails.requires_integration || nodeDetails.integration_hint,
      hasAgentOutput: nodeDetails.requires_agent_output || nodeDetails.agent_output_hint,
      hasAgentInput: nodeDetails.requires_agent_input || nodeDetails.agent_input_hint,
      integrationHint: nodeDetails.integration_hint,
      nodeType: step.node?.type,
      businessFunction: step.node?.business_function,
      position: step.position
    };
  };

  // Constants for gap calculation
  const MIN_GAP = 100;  // Minimum gap between nodes
  const MAX_GAP = 220;  // Maximum gap - truncate with ... if exceeded
  const CHAR_WIDTH = 7;  // Approximate width per character
  const GAP_PADDING = 40;  // Extra padding around label


  // Build connection data from field_sources
  const connections = useMemo(() => {
    if (!agentDefinition?.nodes) return [];

    const conns = [];

    agentDefinition.nodes.forEach((node, nodeIndex) => {
      const fieldSources = node.field_sources || [];

      // Group fields by source
      const sourceGroups = {};
      fieldSources.forEach(fs => {
        const sourceKey = fs.source;
        if (!sourceGroups[sourceKey]) {
          sourceGroups[sourceKey] = {
            source: fs.source,
            sourceType: fs.sourceType || fs.source_type,
            fields: []
          };
        }
        sourceGroups[sourceKey].fields.push(fs.fieldName || fs.field_name);
      });

      // Create connection for each source
      Object.values(sourceGroups).forEach(group => {
        conns.push({
          id: `conn-${group.source}-to-${nodeIndex}`,
          fromNode: group.source, // -1 for agent_input, 0+ for nodes
          toNode: nodeIndex,
          fields: group.fields,
          sourceType: group.sourceType
        });
      });
    });

    // Also add connection from last node to agent_output
    if (agentDefinition.nodes.length > 0) {
      const lastNodeIndex = agentDefinition.nodes.length - 1;
      conns.push({
        id: `conn-${lastNodeIndex}-to-output`,
        fromNode: lastNodeIndex,
        toNode: 'output',
        fields: Object.keys(agentDefinition.agent_output?.schema || {}),
        sourceType: 'nodeOutput'
      });
    }

    return conns;
  }, [agentDefinition]);

  // Calculate gaps based on direct connection labels (content-first approach)
  // Returns array of gaps: [gap after agent-input, gap after node-0, gap after node-1, ...]
  const nodeGaps = useMemo(() => {
    const totalSlots = nodes.length + 1; // agent-input + nodes (agent-output doesn't need gap after it)
    const gaps = new Array(totalSlots).fill(MIN_GAP);

    connections.forEach(conn => {
      // Only consider direct connections (adjacent nodes)
      const fromIdx = conn.fromNode; // -1 for agent-input
      const toIdx = conn.toNode === 'output' ? nodes.length : conn.toNode;
      const skipDistance = toIdx - fromIdx - 1;

      // Only direct connections affect gaps (skip connections go below)
      if (skipDistance === 0) {
        // Calculate required width (show up to 2 fields stacked)
        const displayFields = conn.fields.slice(0, 2);
        const maxLen = Math.max(...displayFields.map(f => f.length));
        const requiredWidth = maxLen * CHAR_WIDTH + GAP_PADDING;

        // Apply min/max constraints
        const gapIndex = fromIdx + 1; // +1 because -1 (agent-input) maps to index 0
        gaps[gapIndex] = Math.max(MIN_GAP, Math.min(MAX_GAP, requiredWidth));
      }
    });

    return gaps;
  }, [connections, nodes.length, MIN_GAP, MAX_GAP, CHAR_WIDTH, GAP_PADDING]);

  // Calculate lanes for skip connections to prevent arrow intersections
  // Overlapping skip connections get different depth lanes
  const skipConnectionLanes = useMemo(() => {
    const lanes = {}; // conn.id -> lane number (0, 1, 2, ...)

    // Get all skip connections with their horizontal span
    const skipConns = connections
      .map(conn => {
        const fromIdx = conn.fromNode;
        const toIdx = conn.toNode === 'output' ? nodes.length : conn.toNode;
        const skipDistance = toIdx - fromIdx - 1;
        if (skipDistance <= 0) return null;
        return {
          id: conn.id,
          from: fromIdx,
          to: toIdx,
          skipDistance
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.from - b.from); // Sort by start position

    // Check if two connections overlap horizontally
    const overlaps = (a, b) => {
      // They overlap if one starts before the other ends
      return !(a.to <= b.from || b.to <= a.from);
    };

    // Assign lanes using greedy algorithm
    skipConns.forEach(conn => {
      // Find lanes used by overlapping connections
      const usedLanes = new Set();
      skipConns.forEach(other => {
        if (other.id !== conn.id && lanes[other.id] !== undefined && overlaps(conn, other)) {
          usedLanes.add(lanes[other.id]);
        }
      });

      // Find lowest available lane
      let lane = 0;
      while (usedLanes.has(lane)) {
        lane++;
      }
      lanes[conn.id] = lane;
    });

    return lanes;
  }, [connections, nodes.length]);

  // Calculate node positions for SVG arrows
  const calculatePositions = useCallback(() => {
    if (!nodesContainerRef.current) return;

    const positions = {};
    const containerRect = nodesContainerRef.current.getBoundingClientRect();

    // Get agent input position
    const agentInputEl = nodesContainerRef.current.querySelector('[data-node-id="agent-input"]');
    if (agentInputEl) {
      const rect = agentInputEl.getBoundingClientRect();
      positions['agent-input'] = {
        x: rect.left - containerRect.left,
        y: rect.top - containerRect.top + rect.height / 2,
        centerX: rect.left - containerRect.left + rect.width / 2,
        centerY: rect.top - containerRect.top + rect.height / 2,
        width: rect.width,
        height: rect.height,
        right: rect.left - containerRect.left + rect.width,
        bottom: rect.top - containerRect.top + rect.height
      };
    }

    // Get each node position
    nodes.forEach((_, index) => {
      const nodeEl = nodesContainerRef.current.querySelector(`[data-node-id="node-${index}"]`);
      if (nodeEl) {
        const rect = nodeEl.getBoundingClientRect();
        positions[`node-${index}`] = {
          x: rect.left - containerRect.left,
          y: rect.top - containerRect.top + rect.height / 2,
          centerX: rect.left - containerRect.left + rect.width / 2,
          centerY: rect.top - containerRect.top + rect.height / 2,
          width: rect.width,
          height: rect.height,
          right: rect.left - containerRect.left + rect.width,
          bottom: rect.top - containerRect.top + rect.height
        };
      }
    });

    // Get agent output position
    const agentOutputEl = nodesContainerRef.current.querySelector('[data-node-id="agent-output"]');
    if (agentOutputEl) {
      const rect = agentOutputEl.getBoundingClientRect();
      positions['agent-output'] = {
        x: rect.left - containerRect.left,
        y: rect.top - containerRect.top + rect.height / 2,
        centerX: rect.left - containerRect.left + rect.width / 2,
        centerY: rect.top - containerRect.top + rect.height / 2,
        width: rect.width,
        height: rect.height
      };
    }

    setNodePositions(positions);
  }, [nodes]);

  useLayoutEffect(() => {
    calculatePositions();
  }, [calculatePositions, agentDefinition]);

  useEffect(() => {
    const timer1 = setTimeout(calculatePositions, 50);
    const timer2 = setTimeout(calculatePositions, 200);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [calculatePositions, agentDefinition]);

  useEffect(() => {
    window.addEventListener('resize', calculatePositions);
    return () => window.removeEventListener('resize', calculatePositions);
  }, [calculatePositions]);

  // Generate sketchy hand-drawn path - matching 8.png style
  // lane parameter determines depth for skip connections (prevents intersections)
  const generatePath = (fromX, fromY, toX, toY, skipDistance = 0, lane = 0, wobbleOffset = 0) => {
    // Add slight wobble for hand-drawn effect
    const w = wobbleOffset * 1.5;

    // Direct connection - slightly curved line (not perfectly straight)
    if (skipDistance === 0) {
      const midX = (fromX + toX) / 2;
      const midY = fromY + w;
      return `M ${fromX} ${fromY + w * 0.5} Q ${midX} ${midY}, ${toX} ${toY - w * 0.3}`;
    }

    // Skip connection - BIG sweeping curve below (wide arc)
    // Base depth + extra depth per lane to prevent intersections
    const baseDepth = 140;
    const depthPerSkip = 60;
    const depthPerLane = 70;  // Extra depth for each lane to avoid overlaps
    const curveDepth = baseDepth + (skipDistance * depthPerSkip) + (lane * depthPerLane);
    const distance = toX - fromX;

    // Control points for smooth bezier curve with slight wobble
    const cp1X = fromX + distance * 0.25 + w;
    const cp1Y = fromY + curveDepth + w * 2;
    const cp2X = toX - distance * 0.25 - w;
    const cp2Y = toY + curveDepth - w;

    return `M ${fromX} ${fromY + w} C ${cp1X} ${cp1Y}, ${cp2X} ${cp2Y}, ${toX} ${toY - w * 0.5}`;
  };

  // Colors matching FlowVisualization
  const arrowColor = isDarkMode ? '#60A5FA' : '#3B82F6';
  const arrowColorHover = isDarkMode ? '#93C5FD' : '#2563EB';

  // Render connection arrow
  const renderConnection = (conn, index) => {
    const fromKey = conn.fromNode === -1 ? 'agent-input' : `node-${conn.fromNode}`;
    const toKey = conn.toNode === 'output' ? 'agent-output' : `node-${conn.toNode}`;

    const fromPos = nodePositions[fromKey];
    const toPos = nodePositions[toKey];

    if (!fromPos || !toPos) return null;

    const isHovered = hoveredConnection === conn.id;
    const color = isHovered ? arrowColorHover : arrowColor;

    const fromX = fromPos.right;
    const fromY = fromPos.centerY;
    const toX = toPos.x;
    const toY = toPos.centerY;

    const fromNodeIndex = conn.fromNode;
    const toNodeIndex = conn.toNode === 'output' ? nodes.length : conn.toNode;
    const skipDistance = toNodeIndex - fromNodeIndex - 1;

    // Get lane for this skip connection (to prevent intersections)
    const lane = skipConnectionLanes[conn.id] || 0;

    // Generate multiple paths with slight variations for sketchy effect
    const path1 = generatePath(fromX, fromY, toX, toY, skipDistance, lane, 0);
    const path2 = generatePath(fromX, fromY, toX, toY, skipDistance, lane, 1);
    const path3 = generatePath(fromX, fromY, toX, toY, skipDistance, lane, -0.7);

    return (
      <g
        key={conn.id}
        onMouseEnter={() => setHoveredConnection(conn.id)}
        onMouseLeave={() => setHoveredConnection(null)}
        style={{ cursor: 'pointer' }}
      >
        {/* Multiple sketchy strokes for hand-drawn effect */}
        <path
          d={path2}
          fill="none"
          stroke={color}
          strokeWidth="1.2"
          strokeLinecap="round"
          opacity="0.3"
        />
        <path
          d={path3}
          fill="none"
          stroke={color}
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.4"
        />
        <path
          d={path1}
          fill="none"
          stroke={color}
          strokeWidth={isHovered ? 1.8 : 1.5}
          strokeLinecap="round"
          className="transition-all duration-150"
        />

        {/* Sketchy arrowhead - calculate angle based on curve direction */}
        {(() => {
          // For skip connections, calculate the angle from the last control point
          let angle = 0; // Default: pointing right (for direct connections)
          if (skipDistance > 0) {
            // Skip connection comes from below - calculate angle from cp2 to endpoint
            const baseDepth = 140;
            const depthPerSkip = 60;
            const depthPerLane = 70;
            const curveDepth = baseDepth + (skipDistance * depthPerSkip) + (lane * depthPerLane);
            const distance = toX - fromX;
            const cp2X = toX - distance * 0.25;
            const cp2Y = toY + curveDepth;
            // Angle from cp2 to endpoint
            angle = Math.atan2(toY - cp2Y, toX - cp2X);
          }

          // Arrowhead size
          const headLen = 10;
          const headAngle = Math.PI / 6; // 30 degrees

          // Calculate arrowhead points
          const x1 = toX - headLen * Math.cos(angle - headAngle);
          const y1 = toY - headLen * Math.sin(angle - headAngle);
          const x2 = toX - headLen * Math.cos(angle + headAngle);
          const y2 = toY - headLen * Math.sin(angle + headAngle);

          return (
            <>
              <path
                d={`M ${x1} ${y1} L ${toX} ${toY} L ${x2} ${y2}`}
                fill="none"
                stroke={color}
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.4"
              />
              <path
                d={`M ${x1 + 1} ${y1} L ${toX + 1} ${toY} L ${x2 + 1} ${y2}`}
                fill="none"
                stroke={color}
                strokeWidth={isHovered ? 1.8 : 1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </>
          );
        })()}

      </g>
    );
  };

  // Calculate label positions for all connections
  const getLabelData = useCallback(() => {
    // Curve depth constants (must match generatePath)
    const baseDepth = 140;
    const depthPerSkip = 60;
    const depthPerLane = 70;

    return connections.map(conn => {
      const fromKey = conn.fromNode === -1 ? 'agent-input' : `node-${conn.fromNode}`;
      const toKey = conn.toNode === 'output' ? 'agent-output' : `node-${conn.toNode}`;

      const fromPos = nodePositions[fromKey];
      const toPos = nodePositions[toKey];

      if (!fromPos || !toPos) return null;

      const fromX = fromPos.right;
      const fromY = fromPos.centerY;
      const toX = toPos.x;

      const fromNodeIndex = conn.fromNode;
      const toNodeIndex = conn.toNode === 'output' ? nodes.length : conn.toNode;
      const skipDistance = toNodeIndex - fromNodeIndex - 1;

      // Get lane for skip connections
      const lane = skipConnectionLanes[conn.id] || 0;

      // Calculate curve depth using same formula as generatePath
      const curveDepth = skipDistance > 0
        ? baseDepth + (skipDistance * depthPerSkip) + (lane * depthPerLane)
        : 0;

      // Both direct and skip: position at midpoint of the connection
      // Direct: slightly above the arrow line
      // Skip: below the curve
      const labelX = (fromX + toX) / 2;  // Always midpoint horizontally
      const labelY = skipDistance > 0
        ? fromY + curveDepth * 0.6 + 30  // Below curve for skip connections
        : fromY - 35;  // Above the arrow line for direct connections

      // Get gap width for this connection (for truncation)
      const gapIndex = fromNodeIndex + 1;
      const gapWidth = skipDistance === 0 ? (nodeGaps[gapIndex] || MIN_GAP) : MAX_GAP;

      return {
        id: conn.id,
        fields: conn.fields,
        x: labelX,
        y: labelY,
        isSkip: skipDistance > 0,
        gapWidth
      };
    }).filter(Boolean);
  }, [connections, nodePositions, nodes.length, nodeGaps, MIN_GAP, MAX_GAP, skipConnectionLanes]);

  const labelData = getLabelData();

  // Sketchy box component - from FlowVisualization
  const SketchyBox = ({ children, color, isSelected, isInput, isOutput, className = '', width = 140, height = 90 }) => {
    const selectedColor = '#FBBF24';
    const inputColor = isDarkMode ? '#06B6D4' : '#0891B2';
    const outputColor = isDarkMode ? '#22C55E' : '#16A34A';
    const strokeColor = isSelected ? selectedColor : isInput ? inputColor : isOutput ? outputColor : (color || (isDarkMode ? '#3B82F6' : '#3B82F6'));
    const w = width, h = height;

    const strokes = [
      {
        top: `M -3 3 Q ${w * 0.3} 1, ${w * 0.6} 4 Q ${w * 0.8} 2, ${w + 4} 2`,
        right: `M ${w - 2} -2 Q ${w - 3} ${h * 0.3}, ${w - 1} ${h * 0.6} Q ${w - 2} ${h * 0.8}, ${w - 3} ${h + 3}`,
        bottom: `M ${w + 2} ${h - 2} Q ${w * 0.7} ${h - 1}, ${w * 0.4} ${h - 3} Q ${w * 0.2} ${h - 1}, -2 ${h - 2}`,
        left: `M 3 ${h + 2} Q 1 ${h * 0.7}, 2 ${h * 0.4} Q 1 ${h * 0.2}, 2 -3`,
        opacity: 0.4
      },
      {
        top: `M -1 1 Q ${w * 0.25} 3, ${w * 0.5} 1 Q ${w * 0.75} 2, ${w + 2} 3`,
        right: `M ${w - 1} -1 Q ${w - 2} ${h * 0.25}, ${w - 3} ${h * 0.5} Q ${w - 1} ${h * 0.75}, ${w - 2} ${h + 2}`,
        bottom: `M ${w + 1} ${h - 1} Q ${w * 0.75} ${h - 3}, ${w * 0.5} ${h - 1} Q ${w * 0.25} ${h - 2}, -1 ${h - 3}`,
        left: `M 1 ${h + 1} Q 3 ${h * 0.75}, 1 ${h * 0.5} Q 2 ${h * 0.25}, 3 -2`,
        opacity: 0.5
      },
      {
        top: `M 0 2 Q ${w * 0.35} 1, ${w * 0.5} 2 Q ${w * 0.65} 1, ${w} 2`,
        right: `M ${w - 2} 0 Q ${w - 1} ${h * 0.35}, ${w - 2} ${h * 0.5} Q ${w - 1} ${h * 0.65}, ${w - 2} ${h}`,
        bottom: `M ${w} ${h - 2} Q ${w * 0.65} ${h - 1}, ${w * 0.5} ${h - 2} Q ${w * 0.35} ${h - 1}, 0 ${h - 2}`,
        left: `M 2 ${h} Q 1 ${h * 0.65}, 2 ${h * 0.5} Q 1 ${h * 0.35}, 2 0`,
        opacity: 0.9
      }
    ];

    return (
      <div className={`relative ${className}`} style={{ width: w, height: h }}>
        <svg
          className="absolute inset-0 pointer-events-none"
          style={{ overflow: 'visible', width: w, height: h }}
          viewBox={`-5 -5 ${w + 10} ${h + 10}`}
        >
          {strokes.map((stroke, i) => (
            <g key={i} opacity={stroke.opacity}>
              <path d={stroke.top} fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" />
              <path d={stroke.right} fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" />
              <path d={stroke.bottom} fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" />
              <path d={stroke.left} fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" />
            </g>
          ))}
          <path d="M -2 6 L 6 -2" fill="none" stroke={strokeColor} strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
          <path d={`M ${w - 6} -2 L ${w + 2} 6`} fill="none" stroke={strokeColor} strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
          <path d={`M ${w + 2} ${h - 6} L ${w - 6} ${h + 2}`} fill="none" stroke={strokeColor} strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
          <path d={`M 6 ${h + 2} L -2 ${h - 6}`} fill="none" stroke={strokeColor} strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
        </svg>
        <div
          className={`w-full h-full rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}
          style={{ width: w, height: h }}
        >
          {children}
        </div>
      </div>
    );
  };

  if (nodes.length === 0) {
    return (
      <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        No workflow nodes to display
      </div>
    );
  }

  const hasMappings = connections.length > 0;

  if (!hasMappings) {
    return (
      <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <p>Variable mappings not yet generated.</p>
        <p className="text-sm mt-2">Run "Map Variables" to see data flow connections.</p>
      </div>
    );
  }

  const firstNodeDetails = getNodeDetails(0);
  const hasAgentInput = firstNodeDetails.hasAgentInput || agentDefinition?.agent_input;

    // Calculate total width based on node sizes + calculated gaps
  const agentInputWidth = 100;
  const agentOutputWidth = 100;
  const nodeWidth = 140;
  const totalGaps = nodeGaps.reduce((sum, gap) => sum + gap, 0);
  const minWidth = agentInputWidth + (nodes.length * nodeWidth) + agentOutputWidth + totalGaps + 80; // +80 for padding

  return (
    <div
      ref={containerRef}
      className={`relative w-full overflow-x-auto rounded-lg ${isDarkMode ? 'bg-gray-800/30' : 'bg-white'}`}
    >
      <div
        ref={nodesContainerRef}
        className="relative p-8"
        style={{ minWidth, minHeight: 450 }}
      >
        {/* SVG layer for arrows */}
        <svg
          ref={svgRef}
          className="absolute inset-0 pointer-events-none"
          style={{ width: '100%', height: '100%', overflow: 'visible' }}
        >
          <g className="pointer-events-auto">
            {connections.map((conn, index) => renderConnection(conn, index))}
          </g>
        </svg>

        {/* HTML layer for labels - rendered outside SVG for proper hover */}
        {labelData.map(label => (
          <div
            key={label.id}
            className="absolute"
            style={{
              left: label.x,
              top: label.y,
              transform: 'translateX(-50%)',
              zIndex: hoveredLabel === label.id ? 50 : 10
            }}
          >
            <div
              className="text-center cursor-pointer"
              onMouseEnter={() => setHoveredLabel(label.id)}
              onMouseLeave={() => setHoveredLabel(null)}
            >
              {hoveredLabel === label.id ? (
                // Expanded tooltip
                <div
                  style={{
                    background: isDarkMode ? 'rgba(17, 24, 39, 0.98)' : 'rgba(255, 255, 255, 0.98)',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: `1px solid ${isDarkMode ? '#4B5563' : '#D1D5DB'}`,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                  }}
                >
                  {label.fields.map((field) => (
                    <div
                      key={field}
                      style={{
                        color: isDarkMode ? '#93C5FD' : '#1D4ED8',
                        fontSize: '12px',
                        lineHeight: '1.7',
                        fontWeight: '500',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {field}
                    </div>
                  ))}
                </div>
              ) : (
                // Default view - truncate based on gap width
                <div>
                  {label.fields.slice(0, 2).map((field) => {
                    const maxChars = Math.floor((label.gapWidth - GAP_PADDING) / CHAR_WIDTH);
                    const displayText = field.length > maxChars ? field.slice(0, maxChars - 3) + '...' : field;
                    return (
                      <div
                        key={field}
                        style={{
                          color: isDarkMode ? '#93C5FD' : '#2563EB',
                          fontSize: '11px',
                          lineHeight: '1.5',
                          fontWeight: '600',
                          whiteSpace: 'nowrap',
                          textShadow: isDarkMode
                            ? '0 1px 4px rgba(0,0,0,1), 0 0 8px rgba(0,0,0,0.8)'
                            : '1px 1px 0 #fff, -1px 1px 0 #fff, 1px -1px 0 #fff, -1px -1px 0 #fff, 0 0 8px rgba(255,255,255,1)'
                        }}
                        title={field}  // Show full text on hover
                      >
                        {displayText}
                      </div>
                    );
                  })}
                  {label.fields.length > 2 && (
                    <div
                      style={{
                        color: isDarkMode ? '#60A5FA' : '#3B82F6',
                        fontSize: '10px',
                        marginTop: '3px',
                        textDecoration: 'underline',
                        fontWeight: '500',
                        textShadow: isDarkMode
                          ? '0 1px 4px rgba(0,0,0,1)'
                          : '1px 1px 0 #fff, -1px 1px 0 #fff, 1px -1px 0 #fff, -1px -1px 0 #fff'
                      }}
                    >
                      +{label.fields.length - 2} more
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Nodes layer - horizontal layout with variable gaps based on label content */}
        <div className="relative flex items-start justify-center pt-16 pb-48">
          {/* AGENT INPUT */}
          {hasAgentInput && (
            <div
              data-node-id="agent-input"
              className="flex flex-col items-center"
              style={{ marginRight: nodeGaps[0] }}
            >
              <SketchyBox isInput width={100} height={70} isSelected={currentNodeIndex === -1}>
                <div
                  className={`w-full h-full flex flex-col items-center justify-center p-2 cursor-pointer transition-transform ${currentNodeIndex === -1 ? 'scale-105' : 'hover:scale-102'}`}
                  onClick={() => onNodeClick?.(-1)}
                >
                  <ArrowDownCircle className={`w-4 h-4 mb-1 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
                  <span className={`text-[10px] font-medium ${isDarkMode ? 'text-cyan-400' : 'text-cyan-700'}`}>
                    AGENT INPUT
                  </span>
                  {agentDefinition?.agent_input && (
                    <span className={`text-[8px] ${isDarkMode ? 'text-cyan-400/70' : 'text-cyan-600/70'}`}>
                      {Object.keys(agentDefinition.agent_input.schema || agentDefinition.agent_input || {}).length} fields
                    </span>
                  )}
                </div>
              </SketchyBox>
            </div>
          )}

          {/* Main nodes */}
          {nodes.map((node, index) => {
            const details = getNodeDetails(index);
            const isSelected = index === currentNodeIndex;
            const nodeColor = details.hasIntegration ? '#F43F5E' : '#3B82F6';
            // Gap after this node (index+1 because agent-input is at index 0)
            const gapAfter = nodeGaps[index + 1] || MIN_GAP;

            return (
              <div
                key={node.nodeId || index}
                data-node-id={`node-${index}`}
                className="flex flex-col items-center"
                style={{ marginRight: index < nodes.length - 1 ? gapAfter : gapAfter }}
              >
                <SketchyBox
                  color={nodeColor}
                  isSelected={isSelected}
                  width={140}
                  height={90}
                >
                  <div
                    className={`w-full h-full p-3 text-center cursor-pointer transition-transform ${isSelected ? 'scale-105' : 'hover:scale-102'}`}
                    onClick={() => onNodeClick?.(index)}
                  >
                    <div className="bg-[#0077A8] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center mx-auto mb-1">
                      {details.position || index + 1}
                    </div>
                    <h5 className={`font-semibold text-xs mb-0.5 leading-tight truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                      {node.alias}
                    </h5>
                    <p className={`text-[10px] truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {details.businessFunction || ''}
                    </p>
                    {details.nodeType === 'promptdel' && (
                      <Zap className="text-blue-400 mx-auto mt-0.5 w-3 h-3" />
                    )}
                  </div>
                </SketchyBox>

                {/* Integration indicator */}
                {details.hasIntegration && (
                  <div className="mt-1.5 flex flex-col items-center">
                    <div className="h-3 w-0.5 bg-rose-500"></div>
                    <div className={`border border-rose-500 rounded-lg p-1 text-center min-w-[70px] ${isDarkMode ? 'bg-rose-900/40' : 'bg-rose-100'}`}>
                      <Database className="w-3 h-3 text-rose-400 mx-auto" />
                      <span className={`text-[9px] font-medium ${isDarkMode ? 'text-rose-400' : 'text-rose-700'}`}>
                        {details.integrationHint?.suggested_source || 'EXT'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* AGENT OUTPUT */}
          <div data-node-id="agent-output" className="flex flex-col items-center">
            <SketchyBox isOutput width={100} height={70} isSelected={currentNodeIndex === nodes.length}>
              <div
                className={`w-full h-full flex flex-col items-center justify-center p-2 cursor-pointer transition-transform ${currentNodeIndex === nodes.length ? 'scale-105' : 'hover:scale-102'}`}
                onClick={() => onNodeClick?.(nodes.length)}
              >
                <ArrowUpCircle className={`w-4 h-4 mb-1 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                <span className={`text-[10px] font-medium ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                  AGENT OUTPUT
                </span>
                {agentDefinition?.agent_output && (
                  <span className={`text-[8px] ${isDarkMode ? 'text-green-400/70' : 'text-green-600/70'}`}>
                    {Object.keys(agentDefinition.agent_output.schema || agentDefinition.agent_output || {}).length} fields
                  </span>
                )}
              </div>
            </SketchyBox>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className={`px-6 pb-4 flex flex-wrap items-center gap-4 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <div className="flex items-center gap-2">
          <svg width="24" height="10">
            <path d="M 0 5 L 18 5" fill="none" stroke={arrowColor} strokeWidth="1.2" />
            <polygon points="24,5 18,2 18,8" fill={arrowColor} />
          </svg>
          <span>Direct flow</span>
        </div>
        <div className="flex items-center gap-2">
          <svg width="28" height="16">
            <path d="M 0 4 Q 14 18, 28 4" fill="none" stroke={arrowColor} strokeWidth="1.2" />
          </svg>
          <span>Skip-node</span>
        </div>
        <div className="ml-auto">
          {connections.length} connection{connections.length !== 1 ? 's' : ''}
        </div>
      </div>
    </div>
  );
};

export default DataFlowVisualization;
